/**
 * Padded chunk neighbourhood for the mesher.
 *
 * Instead of posting nine full chunks to a mesh worker (~0.9 MB structured
 * clone per job), the main thread packs the centre chunk plus a one-voxel
 * border from its eight neighbours into a single 18 x (H+2) x 18 block/light
 * pair and transfers it. The mesher then indexes it with plain strides and no
 * bounds checks: the bottom pad layer is bedrock (culls the world floor), the
 * top pad layer is open sky.
 */

import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import { BEDROCK_ID } from '../blocks';
import { CHUNK_AREA, type World } from './chunk';

export const PAD_W = CHUNK_SIZE + 2;
export const PAD_H = WORLD_HEIGHT + 2;
export const PAD_AREA = PAD_W * PAD_W;
export const PAD_VOLUME = PAD_AREA * PAD_H;
/** Padded index of chunk-local voxel (0,0,0). */
export const PAD_ORIGIN = 1 + PAD_W + PAD_AREA;

/** Light value used where no data exists: full skylight, no blocklight. */
const OPEN_SKY = 0xf0;

export interface PaddedChunk {
  cx: number;
  cz: number;
  blocks: Uint16Array;
  light: Uint8Array;
}

/** Pack chunk (cx,cz) and its neighbours' border voxels. Missing neighbours read as open air. */
export function buildPadded(world: World, cx: number, cz: number): PaddedChunk {
  const blocks = new Uint16Array(PAD_VOLUME);
  const light = new Uint8Array(PAD_VOLUME);
  light.fill(OPEN_SKY);
  // below the world: opaque and dark
  blocks.fill(BEDROCK_ID, 0, PAD_AREA);
  light.fill(0, 0, PAD_AREA);

  for (let dz = -1; dz <= 1; dz++) {
    for (let dx = -1; dx <= 1; dx++) {
      const c = world.getChunk(cx + dx, cz + dz);
      if (!c) continue;
      // local x/z range of the source chunk that falls inside the padded box
      const x0 = dx < 0 ? 15 : 0;
      const x1 = dx > 0 ? 0 : 15;
      const z0 = dz < 0 ? 15 : 0;
      const z1 = dz > 0 ? 0 : 15;
      // padded coordinate of source local 0 along each axis
      const pxo = 1 + dx * CHUNK_SIZE;
      const pzo = 1 + dz * CHUNK_SIZE;
      const sb = c.blocks;
      const sl = c.light;
      for (let y = 0; y < WORLD_HEIGHT; y++) {
        const srcY = y * CHUNK_AREA;
        const dstY = (y + 1) * PAD_AREA;
        for (let z = z0; z <= z1; z++) {
          let si = srcY + z * CHUNK_SIZE + x0;
          let di = dstY + (pzo + z) * PAD_W + pxo + x0;
          for (let x = x0; x <= x1; x++, si++, di++) {
            blocks[di] = sb[si];
            light[di] = sl[si];
          }
        }
      }
    }
  }
  return { cx, cz, blocks, light };
}
